'use client';

import { Breadcrumb, type BreadcrumbItem } from '@paction-2.0/ui';
import Link from 'next/link';

import styles from './styles/breedResultsHeader.module.scss';

export type { BreadcrumbItem };

export type BreedResultsHeaderProps = {
  breedName: string;
  nicknames?: string[] | null;
  provinceName?: string;
  breadcrumbItems: BreadcrumbItem[];
  /**
   * Whether the page has local providers/listings to show. When false, the
   * intro leads with the waitlist outcome instead of "browse listings" — the
   * search results and hero map are suppressed, so an "above" reference would
   * be a broken promise (and an empty map reads as a scarcity signal).
   */
  hasResults?: boolean;
};

export function BreedResultsHeader({
  breedName,
  nicknames,
  provinceName,
  breadcrumbItems,
  hasResults = true
}: BreedResultsHeaderProps) {
  const resolvedBreedName = breedName || '';
  const locationSuffix = provinceName
    ? ` in ${provinceName}`
    : ' across Canada';

  const nicknameText =
    nicknames && nicknames.length > 0 ? nicknames.slice(0, 3).join(', ') : null;

  return (
    <div className={styles.header}>
      <div className={styles.breadcrumbWrapper}>
        <Breadcrumb
          items={breadcrumbItems}
          renderLink={({ href, className, children }) => (
            <Link href={href} className={className}>
              {children}
            </Link>
          )}
        />
      </div>
      <div className={styles.headerText}>
        <h1>
          {resolvedBreedName} puppies for sale{locationSuffix}
        </h1>
        {hasResults ? (
          <p>
            Browse current listings and join the waitlist for{' '}
            {resolvedBreedName} puppies{locationSuffix}.
          </p>
        ) : (
          <p>
            Join the waitlist for {resolvedBreedName} puppies{locationSuffix}{' '}
            and Paction will start matching you with trusted breeders across
            Canada.
          </p>
        )}
        {nicknameText && (
          <p className={styles.alsoKnownAs}>Also known as: {nicknameText}</p>
        )}
      </div>
    </div>
  );
}
